import React, { useState, useEffect } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Container,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Chip,
  TextField,
  InputAdornment,
  Button,
  IconButton,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  Search as SearchIcon,
  MyLocation as MyLocationIcon,
  Restaurant as RestaurantIcon,
  AccessTime as TimeIcon,
  Phone as PhoneIcon,
  Directions as DirectionsIcon,
  ShoppingCart as TakeawayIcon,
  List as ListIcon,
} from '@mui/icons-material';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import TakeawayView from './TakeawayView';
import { apiService, Location } from '../services/api';

// Fix for default markers in react-leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const GuestReservationApp: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
  const [showTakeaway, setShowTakeaway] = useState(false);
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);
  const [clickedLocation, setClickedLocation] = useState<Location | null>(null);
  const [reservationDialogOpen, setReservationDialogOpen] = useState(false);
  const [reservationLocation, setReservationLocation] = useState<Location | null>(null);
  const [timeSlotDialogOpen, setTimeSlotDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const [reservationForm, setReservationForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    date: '',
    time: '18:00',
    guests: 2,
    notes: ''
  });
  const [showMapOnMobile, setShowMapOnMobile] = useState(false);
  const [mapCenter] = useState<[number, number]>([50.8503, 4.3517]); // Belgium center
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);

  // Load locations from API
  useEffect(() => {
    const loadLocations = async () => {
      try {
        const data = await apiService.getLocations();
        setLocations(data);
      } catch (error) {
        console.error('Error loading locations:', error);
      } finally {
        setLoading(false);
      }
    };

    loadLocations();
  }, []);

  const filteredLocations = locations.filter(location =>
    location.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    location.city.toLowerCase().includes(searchQuery.toLowerCase()) ||
    location.address.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleLocationClick = (location: Location) => {
    setClickedLocation(location);
    setLocationDialogOpen(true);
  };

  const handleReservation = (location: Location) => {
    setReservationLocation(location);
    setTimeSlotDialogOpen(true);
    setLocationDialogOpen(false);
  };

  const handleTakeaway = (location: Location) => {
    // TODO: Navigate to takeaway menu
    console.log('Takeaway for:', location.name);
  };

  const handleMyLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          console.log('Current location:', position.coords);
          // TODO: Center map on user location
        },
        (error) => {
          console.error('Error getting location:', error);
        }
      );
    }
  };

  const generateTimeSlots = (date: string) => {
    const slots = [];
    const startHour = 12; // Start om 12:00
    const endHour = 22; // Eind om 22:00
    const slotDuration = 75; // 1u15 in minuten
    
    for (let hour = startHour; hour < endHour; hour++) {
      const time = `${hour.toString().padStart(2, '0')}:00`;
      const endTime = `${(hour + 1).toString().padStart(2, '0')}:15`;
      
      // Simuleer bezette slots (voor demo doeleinden)
      const isOccupied = Math.random() < 0.3; // 30% kans dat een slot bezet is
      
      slots.push({
        time,
        endTime,
        isOccupied,
        display: `${time} - ${endTime}`
      });
    }
    
    return slots;
  };

  const handleTimeSlotSelect = (time: string) => {
    setReservationForm(prev => ({ ...prev, time, date: selectedDate }));
    setTimeSlotDialogOpen(false);
    setReservationDialogOpen(true);
  };

  const handleSubmitReservation = async () => {
    if (!reservationLocation) return;
    
    try {
      const reservationData = {
        locationId: reservationLocation.id,
        locationName: reservationLocation.name,
        customerName: `${reservationForm.firstName} ${reservationForm.lastName}`,
        customerEmail: reservationForm.email,
        customerPhone: reservationForm.phone,
        date: reservationForm.date,
        time: reservationForm.time,
        guests: reservationForm.guests,
        specialRequests: reservationForm.notes
      };
      
      const result = await apiService.submitReservation(reservationData);
      
      if (result.success) {
        // Reset form and close dialog
        setReservationForm({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          date: '',
          time: '18:00',
          guests: 2,
          notes: ''
        });
        setReservationDialogOpen(false);
        setReservationLocation(null);
        
        alert('Reservering succesvol ingediend!');
      } else {
        alert(`Fout: ${result.error}`);
      }
    } catch (error) {
      console.error('Error submitting reservation:', error);
      alert('Er is een fout opgetreden bij het indienen van de reservering.');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {showTakeaway ? (
        <TakeawayView onBack={() => setShowTakeaway(false)} />
      ) : (
        <>
          {/* Header */}
          <AppBar position="static" elevation={0}>
            <Toolbar sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <RestaurantIcon sx={{ fontSize: 32, color: '#000000' }} />
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#000000' }}>
                  Poule&Poulette
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={<TakeawayIcon />}
                onClick={() => setShowTakeaway(true)}
                sx={{ 
                  backgroundColor: '#000000',
                  color: '#ffffff',
                  '&:hover': { backgroundColor: '#333333' }
                }}
              >
                Takeaway
              </Button>
            </Toolbar>
          </AppBar>

          {/* Search Bar */}
          <Box sx={{ p: 4, backgroundColor: '#f8f9fa', borderBottom: '1px solid #e0e0e0' }}>
            <Container maxWidth="lg">
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexDirection: { xs: 'column', md: 'row' } }}>
                <Box sx={{ flex: 1, width: '100%' }}>
                  <TextField
                    fullWidth
                    placeholder="Stad, Postcode"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
                <Box sx={{ minWidth: { md: 200 } }}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<MyLocationIcon />}
                    onClick={handleMyLocation}
                    sx={{ height: 56 }}
                  >
                    Vind mijn locatie
                  </Button>
                </Box>
              </Box>
            </Container>
          </Box>

          {/* Main Content */}
          <Box sx={{ flexGrow: 1, display: 'flex', height: 'calc(100vh - 280px)', mb: 4 }}>
            {/* Location List - Hidden on mobile when map is shown */}
            {(!isMobile || !showMapOnMobile) && (
              <Box sx={{ 
                width: isMobile ? '100%' : 400, 
                borderRight: isMobile ? 'none' : '1px solid #e0e0e0',
                backgroundColor: '#ffffff',
                display: 'flex',
                flexDirection: 'column'
              }}>
                {/* Scrollable List Container */}
                <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
                  <List sx={{ p: 0 }}>
                    {filteredLocations.map((location) => (
                      <ListItem key={location.id} disablePadding>
                        <ListItemButton
                          onClick={() => handleLocationClick(location)}
                                                  sx={{
                          borderBottom: '1px solid #f0f0f0',
                          '&:hover': { backgroundColor: '#f8f9fa' },
                          p: 3
                        }}
                        >
                          <ListItemIcon>
                            <LocationIcon sx={{ color: '#000000' }} />
                          </ListItemIcon>
                          <ListItemText
                                                      primary={
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                              {location.name}
                            </Typography>
                          }
                            secondary={
                              <Box>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                  {location.address}, {location.postcode} {location.city}
                                </Typography>
                                                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              mb: 1,
                              backgroundColor: location.isOpen ? '#e8f5e8' : '#ffeaea',
                              borderRadius: 1,
                              px: 1,
                              py: 0.5,
                              border: `1px solid ${location.isOpen ? '#4caf50' : '#f44336'}`
                            }}>
                              <TimeIcon sx={{ 
                                fontSize: 16, 
                                mr: 0.5, 
                                color: location.isOpen ? '#2e7d32' : '#d32f2f'
                              }} />
                              <Typography variant="body2" sx={{ 
                                fontWeight: 'bold',
                                color: location.isOpen ? '#2e7d32' : '#d32f2f'
                              }}>
                                {location.isOpen ? `Open tot ${location.closingTime}` : 'Gesloten'}
                              </Typography>
                            </Box>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                  <PhoneIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                                  {location.phone}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 0.5, mt: 1, flexWrap: 'wrap' }}>
                                  {location.services.slice(0, 3).map((service) => (
                                    <Chip
                                      key={service}
                                      label={service}
                                      size="small"
                                      variant="outlined"
                                      sx={{ fontSize: '0.6rem', height: 20 }}
                                    />
                                  ))}
                                </Box>
                              </Box>
                            }
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </Box>
            )}

            {/* Map - Always visible on desktop, conditional on mobile */}
            {(!isMobile || showMapOnMobile) && (
              <Box sx={{ flexGrow: 1, height: '100%' }}>
                <MapContainer
                  center={mapCenter}
                  zoom={8}
                  style={{ height: '100%', width: '100%' }}
                >
                  <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  />
                  {locations.map((location) => (
                    <Marker
                      key={location.id}
                      position={location.coordinates}
                      eventHandlers={{
                        click: () => handleLocationClick(location),
                      }}
                    />
                  ))}
                </MapContainer>
              </Box>
            )}
          </Box>

          {/* Mobile Map Toggle */}
          {isMobile && (
            <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={showMapOnMobile ? <ListIcon /> : <LocationIcon />}
                onClick={() => setShowMapOnMobile(!showMapOnMobile)}
              >
                {showMapOnMobile ? 'Toon lijst' : 'Toon kaart'}
              </Button>
            </Box>
          )}

          {/* Footer */}
          <Box sx={{ 
            backgroundColor: '#f8f9fa', 
            borderTop: '1px solid #e0e0e0',
            p: 4,
            mt: 'auto'
          }}>
            <Container maxWidth="lg">
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                flexDirection: { xs: 'column', md: 'row' },
                gap: 4
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <RestaurantIcon sx={{ fontSize: 20, color: '#000000' }} />
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#000000' }}>
                    Poule&Poulette
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  © 2025 Poule&Poulette. Alle rechten voorbehouden.
                </Typography>
                <Box sx={{ display: 'flex', gap: 3 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ cursor: 'pointer', '&:hover': { color: '#000000' } }}>
                    Privacy
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ cursor: 'pointer', '&:hover': { color: '#000000' } }}>
                    Voorwaarden
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ cursor: 'pointer', '&:hover': { color: '#000000' } }}>
                    Contact
                  </Typography>
                </Box>
              </Box>
            </Container>
          </Box>
        </>
      )}

      {/* Location Details Dialog */}
      <Dialog 
        open={locationDialogOpen} 
        onClose={() => setLocationDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        {clickedLocation && (
          <>
            <DialogTitle sx={{ fontWeight: 'bold' }}>
              {clickedLocation.name}
            </DialogTitle>
            <DialogContent>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                  {clickedLocation.address}, {clickedLocation.postcode} {clickedLocation.city}
                </Typography>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  mb: 2,
                  backgroundColor: clickedLocation.isOpen ? '#e8f5e8' : '#ffeaea',
                  borderRadius: 1,
                  px: 1.5,
                  py: 1,
                  border: `2px solid ${clickedLocation.isOpen ? '#4caf50' : '#f44336'}`
                }}>
                  <TimeIcon sx={{ 
                    fontSize: 20, 
                    mr: 1, 
                    color: clickedLocation.isOpen ? '#2e7d32' : '#d32f2f'
                  }} />
                  <Typography variant="body1" sx={{ 
                    fontWeight: 'bold',
                    color: clickedLocation.isOpen ? '#2e7d32' : '#d32f2f'
                  }}>
                    {clickedLocation.isOpen ? `Open tot ${clickedLocation.closingTime}` : 'Gesloten'}
                  </Typography>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <PhoneIcon sx={{ fontSize: 18, mr: 1, verticalAlign: 'middle' }} />
                  {clickedLocation.phone}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                  {clickedLocation.services.map((service) => (
                    <Chip
                      key={service}
                      label={service}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.8rem' }}
                    />
                  ))}
                </Box>
              </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2, gap: 1 }}>
              <Button
                variant="contained"
                onClick={() => {
                  handleReservation(clickedLocation);
                  setLocationDialogOpen(false);
                }}
                sx={{ 
                  backgroundColor: '#000000',
                  color: '#ffffff',
                  '&:hover': { backgroundColor: '#333333' }
                }}
              >
                Reserveren
              </Button>
              <Button
                variant="outlined"
                startIcon={<TakeawayIcon />}
                onClick={() => {
                  handleTakeaway(clickedLocation);
                  setLocationDialogOpen(false);
                }}
              >
                Takeaway
              </Button>
              <Button
                onClick={() => setLocationDialogOpen(false)}
              >
                Sluiten
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Time Slot Selector Dialog */}
      <Dialog 
        open={timeSlotDialogOpen} 
        onClose={() => setTimeSlotDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxWidth: 700,
            width: '100%'
          }
        }}
      >
        {reservationLocation && (
          <>
            <DialogTitle sx={{ 
              fontWeight: 'bold', 
              textAlign: 'center',
              pb: 1,
              '& .MuiTypography-root': {
                fontSize: '1rem',
                color: '#000000'
              }
            }}>
              Kies een tijdsslot - {reservationLocation.name}
            </DialogTitle>
            <DialogContent sx={{ px: 3, pb: 3 }}>
              {/* Date Selector */}
              <Box sx={{ 
                mb: 3, 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center',
                textAlign: 'center'
              }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                  Selecteer een datum
                </Typography>
                <TextField
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ 
                    maxWidth: 200,
                    '& .MuiInputBase-input': {
                      textAlign: 'center'
                    }
                  }}
                />
              </Box>

              {/* Time Slots Grid */}
              {selectedDate && (
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                    Beschikbare tijdsslots
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 3, color: '#666' }}>
                    Elke reservering duurt 1 uur en 15 minuten
                  </Typography>
                  
                  <Box sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)' }, 
                    gap: 2 
                  }}>
                    {generateTimeSlots(selectedDate).map((slot) => (
                      <Button
                        key={slot.time}
                        variant={slot.isOccupied ? "outlined" : "contained"}
                        onClick={() => !slot.isOccupied && handleTimeSlotSelect(slot.time)}
                        disabled={slot.isOccupied}
                        sx={{
                          py: 2,
                          px: 2,
                          fontSize: '0.875rem',
                          fontWeight: 'bold',
                          backgroundColor: slot.isOccupied ? '#f5f5f5' : '#000000',
                          color: slot.isOccupied ? '#999' : '#ffffff',
                          border: slot.isOccupied ? '1px solid #ddd' : 'none',
                          '&:hover': {
                            backgroundColor: slot.isOccupied ? '#f5f5f5' : '#333333'
                          },
                          '&:disabled': {
                            backgroundColor: '#f5f5f5',
                            color: '#999'
                          }
                        }}
                      >
                        {slot.display}
                        {slot.isOccupied && (
                          <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontSize: '0.7rem' }}>
                            Bezet
                          </Typography>
                        )}
                      </Button>
                    ))}
                  </Box>
                </Box>
              )}
            </DialogContent>
            <DialogActions sx={{ p: 3, pt: 0 }}>
              <Button
                onClick={() => setTimeSlotDialogOpen(false)}
                sx={{ color: '#666' }}
              >
                Annuleren
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Reservation Form Dialog */}
      <Dialog 
        open={reservationDialogOpen} 
        onClose={() => setReservationDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxWidth: 600,
            width: '100%'
          }
        }}
      >
        {reservationLocation && (
          <>
            <DialogTitle sx={{ 
              fontWeight: 'bold', 
              textAlign: 'center',
              pb: 1,
              '& .MuiTypography-root': {
                fontSize: '0.875rem',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                color: '#666'
              }
            }}>
              RESERVERING
            </DialogTitle>
            <DialogContent sx={{ px: 3, pb: 3 }}>
              {/* Reservation Summary */}
              <Box sx={{ mb: 3, textAlign: 'center' }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Reservering voor {reservationForm.guests} personen, vandaag om {reservationForm.time || '18:00'} uur.
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Je reservering eindigt om:
                </Typography>
                <Box sx={{ 
                  backgroundColor: '#2c2c2c', 
                  color: '#ffffff', 
                  py: 1, 
                  px: 2, 
                  borderRadius: 1,
                  display: 'inline-block',
                  fontWeight: 'bold'
                }}>
                  {reservationForm.time ? 
                    new Date(`2000-01-01T${reservationForm.time}`).getHours() + 1 + ':15 uur' : 
                    '19:15 uur'
                  }
                </Box>
              </Box>

              {/* Customer Details Section */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Je gegevens
                </Typography>
                <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
                  Je ontvangt een bevestiging van je reservering per e-mail.
                </Typography>

                {/* Salutation Radio Buttons */}
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    {['Dhr.', 'Mevr.', 'Mx.', 'Familie', 'Bedrijf'].map((salutation) => (
                      <Box key={salutation} sx={{ display: 'flex', alignItems: 'center' }}>
                        <input
                          type="radio"
                          id={salutation}
                          name="salutation"
                          value={salutation}
                          style={{ marginRight: '4px' }}
                        />
                        <label htmlFor={salutation} style={{ fontSize: '0.875rem' }}>
                          {salutation}
                        </label>
                      </Box>
                    ))}
                  </Box>
                </Box>

                {/* Name and Contact Fields */}
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                  <TextField
                    label="Voornaam"
                    value={reservationForm.firstName || ''}
                    onChange={(e) => setReservationForm({...reservationForm, firstName: e.target.value})}
                    fullWidth
                    size="small"
                  />
                  <TextField
                    label="Achternaam"
                    value={reservationForm.lastName || ''}
                    onChange={(e) => setReservationForm({...reservationForm, lastName: e.target.value})}
                    fullWidth
                    size="small"
                  />
                  <TextField
                    label="E-mail"
                    type="email"
                    value={reservationForm.email}
                    onChange={(e) => setReservationForm({...reservationForm, email: e.target.value})}
                    fullWidth
                    size="small"
                  />
                  <TextField
                    label="Telefoonnummer"
                    value={reservationForm.phone}
                    onChange={(e) => setReservationForm({...reservationForm, phone: e.target.value})}
                    fullWidth
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                          <Box sx={{ 
                            width: 16, 
                            height: 12, 
                            background: 'linear-gradient(to right, #000 33%, #ffd700 33% 66%, #ff0000 66%)',
                            borderRadius: '1px',
                            mr: 0.5
                          }} />
                          <Typography variant="caption">+32</Typography>
                        </Box>
                      ),
                    }}
                  />
                </Box>

                {/* Notes Field */}
                <TextField
                  label="Heb je belangrijke dieetwensen, allergieën of andere opmerkingen?"
                  multiline
                  rows={3}
                  value={reservationForm.notes}
                  onChange={(e) => setReservationForm({...reservationForm, notes: e.target.value})}
                  fullWidth
                  size="small"
                />
              </Box>

              {/* Marketing Opt-in */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <input
                    type="checkbox"
                    id="marketing"
                    defaultChecked
                    style={{ marginRight: '8px' }}
                  />
                  <label htmlFor="marketing" style={{ fontSize: '0.875rem' }}>
                    Mis niks van de nieuwste events, promoties en arrangementen van P&P Gent
                  </label>
                </Box>
              </Box>
            </DialogContent>
            <DialogActions sx={{ p: 3, pt: 0 }}>
              <Button
                variant="contained"
                onClick={handleSubmitReservation}
                disabled={!reservationForm.firstName || !reservationForm.lastName || !reservationForm.email || !reservationForm.phone}
                fullWidth
                sx={{ 
                  backgroundColor: '#8B4513',
                  color: '#ffffff',
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  '&:hover': { backgroundColor: '#A0522D' },
                  '&:disabled': { backgroundColor: '#ccc' }
                }}
              >
                Reserveer nu
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default GuestReservationApp;
